/**
 * MarketTrendsChart Component
 *
 * A single combined line chart showing whether a Region/District is
 * currently trending toward a buyer's market, seller's market, or
 * in-between - each metric indexed onto one shared axis (see utils.ts),
 * plus an equal-weight "Overall" line and a checkbox per metric so a
 * reader can isolate any subset. See types.ts for why this is a separate
 * component from SalesChart, and for the indexing rationale.
 */
import { Chart, LineController, PointElement, LineElement, LinearScale, TimeScale, Tooltip, } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { METRIC_CONFIGS, OVERALL_CONFIG, MARKET_STATUS_COLORS, PROPERTY_SEGMENTS } from './types.js';
import { computeMarketIndex, clampIndex, describeBandRanges } from './utils.js';
import { injectStyles } from './styles.js';
import { Disclaimers } from '../PropertyModal/Disclaimers.js';
Chart.register(LineController, PointElement, LineElement, LinearScale, TimeScale, Tooltip);
// The shared index axis: +-1 is always exactly each metric's own
// threshold (see utils.ts), so a single fixed band boundary works for
// every line at once - that's the entire point of normalizing first.
// INDEX_CLAMP (what data gets clamped to) is smaller than AXIS_LIMIT
// (the chart's actual y range), so a clamped line sits with visible
// headroom above/below it instead of pinned flush against the edge.
const INDEX_CLAMP = 2.5;
const AXIS_LIMIT = 3.2;
const BAND_BOUNDARY = 1;
// Shades the three zones (beforeDraw, so they sit behind the data lines)
// and labels them directly on the canvas (afterDraw, so the label text
// paints on TOP of the lines instead of having a line drawn through the
// middle of it - the labels were getting visually cut in half by
// whichever line happened to cross through that y-value). The numeric
// index values aren't meaningful to a reader on their own, only the zone
// a line is in, so there are no numeric y-axis ticks at all.
const zoneBandPlugin = {
    id: 'market-zone-bands',
    beforeDraw(chart) {
        const { ctx, chartArea, scales } = chart;
        if (!chartArea)
            return;
        const yScale = scales.y;
        const drawZone = (from, to, color) => {
            const yTop = yScale.getPixelForValue(to);
            const yBottom = yScale.getPixelForValue(from);
            ctx.save();
            ctx.fillStyle = color;
            ctx.globalAlpha = 0.08;
            ctx.fillRect(chartArea.left, yTop, chartArea.right - chartArea.left, yBottom - yTop);
            ctx.restore();
        };
        drawZone(BAND_BOUNDARY, AXIS_LIMIT, MARKET_STATUS_COLORS.seller);
        drawZone(-BAND_BOUNDARY, BAND_BOUNDARY, MARKET_STATUS_COLORS.balanced);
        drawZone(-AXIS_LIMIT, -BAND_BOUNDARY, MARKET_STATUS_COLORS.buyer);
    },
    afterDraw(chart) {
        const { ctx, chartArea, scales } = chart;
        if (!chartArea)
            return;
        const yScale = scales.y;
        const labelX = chartArea.left + 8;
        ctx.save();
        ctx.font = '600 11px -apple-system, BlinkMacSystemFont, sans-serif';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = MARKET_STATUS_COLORS.seller;
        ctx.fillText("Seller's Market", labelX, yScale.getPixelForValue((BAND_BOUNDARY + AXIS_LIMIT) / 2));
        ctx.fillStyle = '#64748b';
        ctx.fillText('Balanced', labelX, yScale.getPixelForValue(0));
        ctx.fillStyle = MARKET_STATUS_COLORS.buyer;
        ctx.fillText("Buyer's Market", labelX, yScale.getPixelForValue(-(BAND_BOUNDARY + AXIS_LIMIT) / 2));
        ctx.restore();
    },
};
export class MarketTrendsChart {
    data;
    container;
    charts = [];
    visibleMetrics;
    overallVisible = true;
    propertySegment = 'all';
    helpVisible = false;
    disclaimers = null;
    constructor(options) {
        this.data = options.data;
        this.container = options.container;
        // Individual metrics start hidden - Overall (always on) is the
        // headline view; the component lines are for drilling in on demand.
        this.visibleMetrics = new Set();
        this.preprocessSnlr();
        injectStyles();
        this.render();
    }
    updateData(data) {
        this.data = data;
        this.preprocessSnlr();
        this.render();
    }
    destroy() {
        this.charts.forEach((c) => c.destroy());
        this.charts = [];
        this.disclaimers?.destroy();
        this.disclaimers = null;
        this.container.innerHTML = '';
    }
    currentPoints() {
        return this.data[this.propertySegment];
    }
    // SNLR isn't returned by the API - both inputs (soldCount,
    // newListingCount) already are, so it's computed here once per segment
    // rather than adding a backend field for a one-line division.
    //
    // MIN_SOLD_SAMPLE mirrors mls-sync's computeMarketTrends floor of the
    // same name (median DOM / sale-to-list / months-of-supply) - a scope-
    // month with 1-2 sales can otherwise produce a wildly misleading ratio
    // (e.g. 1 sold / 1 new listing = a "perfect" 100% SNLR from one data
    // point) the same way pct_price_drops did before that guard existed.
    preprocessSnlr() {
        const MIN_SOLD_SAMPLE = 5;
        for (const segment of PROPERTY_SEGMENTS) {
            for (const p of this.data[segment.key]) {
                if (typeof p.soldCount === 'number' &&
                    p.soldCount >= MIN_SOLD_SAMPLE &&
                    typeof p.newListingCount === 'number' &&
                    p.newListingCount > 0) {
                    p.snlr = p.soldCount / p.newListingCount;
                }
            }
        }
    }
    render() {
        this.charts.forEach((c) => c.destroy());
        this.charts = [];
        if (this.currentPoints().length === 0) {
            this.container.innerHTML = `
        <div class="sm-market-trends">
          ${this.buildSegmentRowHtml()}
          ${this.helpVisible ? this.buildHelpPanelHtml() : ''}
          <div class="sm-market-trends-empty">Not enough sales history yet to show market trends for this property type.</div>
        </div>
      `;
            this.attachSegmentListeners();
            this.attachHelpListener();
            return;
        }
        this.container.innerHTML = `
      <div class="sm-market-trends">
        ${this.buildSegmentRowHtml()}
        ${this.helpVisible ? this.buildHelpPanelHtml() : ''}
        <div class="sm-market-trends-main-card">
          <div class="sm-market-trends-canvas-container-main">
            <canvas id="market-chart-main"></canvas>
          </div>
        </div>
        <div class="sm-market-trends-controls">
          ${this.buildCheckboxHtml('__overall', OVERALL_CONFIG.label, OVERALL_CONFIG.color, this.overallVisible)}
          ${METRIC_CONFIGS.map((c) => this.buildCheckboxHtml(String(c.key), c.label, c.color, this.visibleMetrics.has(String(c.key)))).join('')}
        </div>
        <div id="sm-market-trends-disclaimers"></div>
      </div>
    `;
        this.attachSegmentListeners();
        this.attachHelpListener();
        this.attachControlListeners();
        this.createMainChart();
        // Aggregate-data disclaimer replaces idx as the always-visible one
        // (see NeighborhoodModal's matching comment); mls/general/ai stay
        // behind the toggle. Re-created each render() since this component
        // fully rebuilds its innerHTML on every data/segment change.
        const disclaimersContainer = this.container.querySelector('#sm-market-trends-disclaimers');
        if (disclaimersContainer) {
            this.disclaimers?.destroy();
            this.disclaimers = new Disclaimers({
                container: disclaimersContainer,
                showIdx: false,
                showAggregate: true,
            });
            this.disclaimers.render();
        }
    }
    buildSegmentRowHtml() {
        return `
      <div class="sm-market-trends-segment-row">
        <div class="sm-market-trends-segment-group">
          <span class="sm-market-trends-segment-label">Property Type</span>
          <div class="sm-market-trends-segment-btns">
            ${PROPERTY_SEGMENTS.map((s) => `
              <button class="sm-market-trends-segment-btn${s.key === this.propertySegment ? ' active' : ''}" data-segment="${s.key}">
                ${s.label}
              </button>
            `).join('')}
          </div>
        </div>
        <button
          class="sm-market-trends-help-btn"
          type="button"
          aria-expanded="${this.helpVisible}"
          aria-label="What do these metrics mean?"
          title="What do these metrics mean?"
        >?</button>
      </div>
    `;
    }
    // Every metric's description + its buyer/balanced/seller cutoffs, read
    // straight from METRIC_CONFIGS/describeBandRanges - the same numbers
    // driving the chart, not a hand-copied second source that can drift.
    buildHelpPanelHtml() {
        const items = METRIC_CONFIGS.map((c) => {
            const ranges = c.bands ? describeBandRanges(c.bands, c.format) : null;
            return `
        <div class="sm-market-trends-help-item">
          <div class="sm-market-trends-help-item-header">
            <span class="sm-market-trends-swatch" style="background:${c.color}" aria-hidden="true"></span>
            <span class="sm-market-trends-help-item-label">${c.label}</span>
          </div>
          <p class="sm-market-trends-help-item-desc">${c.description}</p>
          ${ranges ? `
            <div class="sm-market-trends-help-item-ranges">
              <span class="sm-market-trends-help-range buyer">Buyer's: ${ranges.buyer}</span>
              <span class="sm-market-trends-help-range balanced">Balanced: ${ranges.balanced}</span>
              <span class="sm-market-trends-help-range seller">Seller's: ${ranges.seller}</span>
            </div>
          ` : ''}
        </div>
      `;
        }).join('');
        return `
      <div class="sm-market-trends-help-panel">
        <p class="sm-market-trends-help-overall">
          <strong>${OVERALL_CONFIG.label}</strong> equally averages the index values of every metric below that has data for a given month - it isn't a real-world unit on its own, just a headline read on overall market direction.
        </p>
        ${items}
      </div>
    `;
    }
    attachSegmentListeners() {
        const buttons = this.container.querySelectorAll('.sm-market-trends-segment-btn');
        buttons.forEach((btn) => {
            btn.addEventListener('click', () => {
                const segment = btn.dataset.segment;
                if (segment === this.propertySegment)
                    return;
                this.propertySegment = segment;
                this.render();
            });
        });
    }
    attachHelpListener() {
        const btn = this.container.querySelector('.sm-market-trends-help-btn');
        btn?.addEventListener('click', () => {
            this.helpVisible = !this.helpVisible;
            this.render();
        });
    }
    // A plain color dot (matching the chart's point markers), not a dashed
    // line snippet - a small dashed segment was hard to read at a glance in
    // a dense list; a solid dot scans much faster top-to-bottom.
    buildCheckboxHtml(key, label, color, checked) {
        return `
      <label class="sm-market-trends-checkbox-item" data-metric="${key}">
        <input type="checkbox" ${checked ? 'checked' : ''}>
        <span class="sm-market-trends-swatch" style="background:${color}" aria-hidden="true"></span>
        <span class="sm-market-trends-checkbox-label">${label}</span>
      </label>
    `;
    }
    attachControlListeners() {
        const items = this.container.querySelectorAll('.sm-market-trends-checkbox-item');
        items.forEach((item) => {
            const input = item.querySelector('input[type="checkbox"]');
            const metric = item.dataset.metric;
            if (!input || !metric)
                return;
            input.addEventListener('change', () => {
                if (metric === '__overall') {
                    this.overallVisible = input.checked;
                }
                else if (input.checked) {
                    this.visibleMetrics.add(metric);
                }
                else {
                    this.visibleMetrics.delete(metric);
                }
                this.render();
            });
        });
    }
    buildIndexedSeries(config) {
        if (!config.bands)
            return [];
        return this.currentPoints()
            .filter((p) => typeof p[config.key] === 'number')
            .map((p) => {
            const rawValue = p[config.key];
            const index = clampIndex(computeMarketIndex(rawValue, config.bands), INDEX_CLAMP);
            return { x: new Date(p.month).getTime(), y: index, rawValue };
        });
    }
    // Always averages every metric that has data, regardless of which
    // individual lines are currently checked for display - it's meant to be
    // a stable headline reference, not something that silently changes
    // meaning (or goes empty) as a reader toggles component lines on/off.
    computeOverallSeries(seriesByKey) {
        const allSeries = Array.from(seriesByKey.values());
        if (allSeries.length === 0)
            return [];
        const byMonth = new Map();
        for (const series of allSeries) {
            for (const pt of series) {
                if (!byMonth.has(pt.x))
                    byMonth.set(pt.x, []);
                byMonth.get(pt.x).push(pt.y);
            }
        }
        return Array.from(byMonth.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([x, values]) => {
            const avg = clampIndex(values.reduce((sum, v) => sum + v, 0) / values.length, INDEX_CLAMP);
            return { x, y: avg, rawValue: avg };
        });
    }
    createMainChart() {
        const canvas = this.container.querySelector('#market-chart-main');
        if (!canvas)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        const seriesByKey = new Map();
        for (const config of METRIC_CONFIGS) {
            const series = this.buildIndexedSeries(config);
            if (series.length > 0)
                seriesByKey.set(String(config.key), series);
        }
        // datasetConfigs stays in lockstep with the metric portion of
        // `datasets` (same filter, same order) so the tooltip callback's
        // datasetIndex lookup stays correct even when a metric has no data
        // points this scope/window and gets dropped - indexing back into the
        // unfiltered METRIC_CONFIGS would silently mislabel every dataset
        // after the first gap. Overall is handled separately in the tooltip
        // (it has no raw unit of its own), so it's never pushed here.
        const datasetConfigs = [];
        const datasets = [];
        for (const config of METRIC_CONFIGS) {
            const series = seriesByKey.get(String(config.key));
            if (!series)
                continue;
            datasetConfigs.push(config);
            datasets.push({
                label: config.label,
                data: series,
                borderColor: config.color,
                backgroundColor: config.color,
                borderWidth: 2,
                borderDash: config.dash,
                pointRadius: 3,
                pointHoverRadius: 5,
                tension: 0.15,
                spanGaps: true,
                hidden: !this.visibleMetrics.has(String(config.key)),
            });
        }
        const overallSeries = this.computeOverallSeries(seriesByKey);
        if (overallSeries.length > 0) {
            datasets.push({
                label: OVERALL_CONFIG.label,
                data: overallSeries,
                borderColor: OVERALL_CONFIG.color,
                backgroundColor: OVERALL_CONFIG.color,
                borderWidth: OVERALL_CONFIG.lineWidth,
                pointRadius: 3,
                pointHoverRadius: 5,
                tension: 0.15,
                spanGaps: true,
                hidden: !this.overallVisible,
            });
        }
        if (datasets.length === 0)
            return;
        const chart = new Chart(ctx, {
            type: 'line',
            data: { datasets },
            plugins: [zoneBandPlugin],
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'nearest', intersect: false },
                scales: {
                    x: {
                        type: 'time',
                        time: { unit: 'month', displayFormats: { month: 'MMM yy' } },
                        grid: { display: false },
                        ticks: { maxTicksLimit: 10, font: { size: 10 } },
                    },
                    y: {
                        min: -AXIS_LIMIT,
                        max: AXIS_LIMIT,
                        grid: { color: MARKET_STATUS_COLORS.gridLines },
                        ticks: { display: false },
                    },
                },
                plugins: {
                    legend: { display: false }, // the checkbox row is the legend
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                if (context.dataset.label === OVERALL_CONFIG.label) {
                                    const y = context.parsed.y;
                                    const zone = y > BAND_BOUNDARY ? "Seller's Market" : y < -BAND_BOUNDARY ? "Buyer's Market" : 'Balanced';
                                    return `Overall: ${zone} (${y.toFixed(2)})`;
                                }
                                const point = context.raw;
                                const config = datasetConfigs[context.datasetIndex];
                                if (!config)
                                    return '';
                                return `${config.label}: ${config.format(point.rawValue)}`;
                            },
                        },
                    },
                },
            },
        });
        this.charts.push(chart);
    }
}
//# sourceMappingURL=MarketTrendsChart.js.map